import React from 'react';
import PropTypes from 'prop-types';
import { NavLink, useLocation } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import Drawer from '@mui/material/Drawer';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Box from '@mui/material/Box';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { ErrorOutlineTwoTone, PowerSettingsNew } from '@mui/icons-material';
import { isImpersonated } from 'helpers/impersonate';
import sidebarStyle from 'components/Sidebar/style/sidebarStyle';
import PerfectScrollbar from 'react-perfect-scrollbar';
import 'react-perfect-scrollbar/dist/css/styles.css';
import { isPerfectScrollbar } from 'helpers/helpers';
import { isPast } from 'date-fns';
import { tutorGuide } from '../../data';
import { drawerWidth } from 'assets/styles/jss/main';
import useLogout from '../../hooks/useLogout';

const s = sidebarStyle;

const paperSx = {
  ...s.whiteBackground,
  border: 'none',
  position: 'fixed',
  top: 0,
  bottom: 0,
  left: 0,
  zIndex: 1032,
  transitionProperty: 'top, bottom, width',
  transitionDuration: '.2s, .2s, .35s',
  transitionTimingFunction: 'linear, linear, ease',
  boxShadow: 'none',
  width: drawerWidth,
  height: '100%',
  '&:before,&:after': {
    position: 'absolute',
    zIndex: '-1',
    width: '100%',
    height: '100%',
    content: '""',
    display: 'block',
    top: '0',
  },
};

const paperRightSx = {
  ...paperSx,
  right: 0,
  left: 'auto',
};

const Background = styled('div')(s.background);
const SidebarWrapper = styled('div')(s.sidebarWrapper);
const UserWrapper = styled('div')(s.user);

const StyledList = styled(List, {
  shouldForwardProp: (prop) => prop !== 'whiteSep',
})(({ whiteSep }) => ({
  ...s.list,
  ...(whiteSep && s.whiteSep),
}));

const StyledListItem = styled(ListItem, {
  shouldForwardProp: (prop) => !['welcomeItem', 'userItem', 'functionItem'].includes(prop),
})(({ welcomeItem, userItem, functionItem }) => ({
  ...s.item,
  ...(welcomeItem && s.welcomeItem),
  ...(userItem && s.userItem),
  ...(functionItem && s.functionItem),
}));

const StyledListItemIcon = styled(ListItemIcon)(s.itemIcon);

const StyledListItemText = styled(ListItemText, {
  shouldForwardProp: (prop) => !['active', 'userItemText'].includes(prop),
})(({ active, userItemText }) => ({
  ...s.itemText,
  ...(active && s.itemTextActive),
  ...(userItemText && s.userItemText),
}));

const StyledNavLink = styled(NavLink)(s.itemLink);

const RedCircleAlert = styled(ErrorOutlineTwoTone, {
  shouldForwardProp: (prop) => prop !== 'visible',
})(({ visible }) => ({
  ...(visible ? s.redCircleAlert : s.disabled),
}));

const Sidebar = ({ context, handleDrawerClose, handleDrawerToggle, newHeader, open, image, routes }) => {
  const location = useLocation();
  const { account } = context.state.user;
  const logout = useLogout(handleDrawerClose);

  const handleNavClick = (e) => {
    const clickOnIcon = e.target.className instanceof SVGAnimatedString;
    const clickOnClickable = e.target.dataset.clickable === 'true';
    if (clickOnIcon || clickOnClickable) handleDrawerClose();
  };

  const checkIfAccepted = () => context.state.user.hired;
  const checkInternshipIsOver = () => checkIfAccepted() && isPast(Date.parse(account.internship_period[1]));

  const activeRoute = (routeName) => {
    if (routeName === '/annonces' && location.pathname.split('/')[1] === 'details-annonce') {
      return true;
    }
    return location.pathname.indexOf(routeName) > -1;
  };

  const handleNavLink = (e, disabled = false) => {
    disabled && e.preventDefault();
  };

  const renderName = () => {
    const { type, first_name, last_name, college } = account;
    if (['student', 'pro', 'hr', 'guest', 'association'].includes(type)) {
      return `${first_name.toUpperCase()} ${last_name.toUpperCase()}`;
    } else if (type === 'college') {
      return college.name.toUpperCase();
    } else if (type === 'administrator') {
      return `${first_name.toUpperCase()}`;
    }
  };

  const user = (
    <UserWrapper>
      <StyledList whiteSep>
        <StyledListItem welcomeItem>Bienvenue,</StyledListItem>
        <StyledListItem userItem>
          <StyledListItemText primary={renderName()} disableTypography userItemText />
        </StyledListItem>
        <StyledListItem functionItem>
          {account.type === 'hr' && account.company.name}
          {/* TODO: comprendre pourquoi account.company n'est pas un object  */}
          {account.type === 'pro' && account.company.name}
          {account.type === 'student' && account.college.name}
          {account.type === 'association' && account.association.name.split('à')[0].replace('Association ', '')}
        </StyledListItem>

        {routes.map((prop, key) => {
          if (prop.pathTo || !prop.user || prop.path === '/deconnexion') return null;

          let disabledLink = false;
          if (account.type === 'pro' && !account.has_signed_charter) {
            disabledLink = true;
            if (prop.path === '/modifier-profil' && isImpersonated()) {
              disabledLink = false;
            }
          }

          const mustNotifyProToCheckProfile =
            prop?.path === '/modifier-profil' &&
            !disabledLink &&
            account.type === 'pro' &&
            account.mustSeeImageRightsAndNotifSettings;

          return (
            <StyledListItem key={key} onClick={handleDrawerToggle}>
              <StyledNavLink
                to={prop.path}
                className={disabledLink ? 'disabled' : ''}
                onClick={(e) => {
                  e.stopPropagation();
                  handleNavLink(e, disabledLink);
                }}>
                <StyledListItemIcon>
                  <prop.icon />
                </StyledListItemIcon>
                <StyledListItemText primary={prop.name} disableTypography active={activeRoute(prop.path)} />
                <RedCircleAlert visible={mustNotifyProToCheckProfile} />
              </StyledNavLink>
            </StyledListItem>
          );
        })}
      </StyledList>
    </UserWrapper>
  );

  const links = (
    <StyledList whiteSep>
      {routes.map((prop, key) => {
        if (prop.user || prop.redirect || prop.invisible) return null;

        let disabledLink = false;
        if (account.type === 'pro' && !account.has_signed_charter) {
          if ((prop.path !== '/charte' && !isImpersonated()) || (prop.path !== '/annonces' && isImpersonated())) {
            disabledLink = true;
          }
        }

        if (account.type === 'student') {
          if (prop.path === '/rapport') {
            disabledLink = true;
            if (checkInternshipIsOver()) disabledLink = false;
          }
          if (prop.path === '/messages') {
            disabledLink = true;
            if (checkIfAccepted() || context.state.user.has_messages) disabledLink = false;
          }
        }

        return (
          <StyledListItem key={key}>
            <StyledNavLink
              data-clickable
              to={prop.path}
              className={disabledLink ? 'disabled' : ''}
              onClick={(e) => handleNavLink(e, disabledLink)}>
              <StyledListItemIcon>
                <prop.icon />
              </StyledListItemIcon>
              <StyledListItemText
                data-clickable
                primary={prop.name}
                disableTypography
                active={activeRoute(prop.path)}
              />
            </StyledNavLink>
          </StyledListItem>
        );
      })}
    </StyledList>
  );

  const logoutJsx = (
    <StyledList>
      {account.type === 'pro' && (
        <StyledListItem>
          <Box
            component='a'
            href={tutorGuide}
            target='_blank'
            rel='noopener noreferrer'
            sx={{
              display: 'flex',
              alignItems: 'center',
              color: 'inherit',
              textDecoration: 'none',
              '&:hover': {
                color: 'inherit',
              },
            }}>
            <StyledListItemIcon>
              <OpenInNewIcon />
            </StyledListItemIcon>
            <StyledListItemText primary='Guide Tuteur' disableTypography />
          </Box>
        </StyledListItem>
      )}
      <StyledListItem>
        <StyledNavLink onClick={logout}>
          <StyledListItemIcon>
            <PowerSettingsNew />
          </StyledListItemIcon>
          <StyledListItemText primary='Déconnexion' disableTypography />
        </StyledNavLink>
      </StyledListItem>
    </StyledList>
  );

  const brand = (
    <a href={__ENV__.PUBLIC_URL} style={{ margin: '20px auto' }}>
      <img style={{ width: '150px' }} src={`${__ENV__.PUBLIC_URL}/logo-white-sidebar.svg`} alt='logo' />
    </a>
  );

  const iOS = typeof navigator !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent);

  const scrollableContent = (
    <>
      {user}
      {links}
      {logoutJsx}
    </>
  );

  const drawerContent = (
    <>
      {brand}
      {isPerfectScrollbar() ? (
        <SidebarWrapper>
          <PerfectScrollbar>{scrollableContent}</PerfectScrollbar>
        </SidebarWrapper>
      ) : (
        <SidebarWrapper className='scrollbar'>{scrollableContent}</SidebarWrapper>
      )}
      {image !== undefined && <Background style={{ backgroundImage: `url(${image})` }} />}
    </>
  );

  return (
    <div>
      <Box sx={{ display: { xs: 'block', md: 'none' } }}>
        <SwipeableDrawer
          disableBackdropTransition={!iOS}
          disableDiscovery={iOS}
          variant='temporary'
          anchor={newHeader ? 'left' : 'right'}
          open={open}
          slotProps={{
            paper: { sx: newHeader ? paperSx : paperRightSx },
          }}
          onClick={handleNavClick}
          onOpen={() => {}}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}>
          {drawerContent}
        </SwipeableDrawer>
      </Box>
      <Box sx={{ display: { xs: 'none', md: 'block' } }}>
        <Drawer
          anchor='left'
          variant='permanent'
          open
          slotProps={{
            paper: { sx: paperSx },
          }}
          onClick={handleNavClick}>
          {brand}
          {isPerfectScrollbar() ? (
            <SidebarWrapper>
              <PerfectScrollbar>{scrollableContent}</PerfectScrollbar>
            </SidebarWrapper>
          ) : (
            <SidebarWrapper className='scrollbar'>{scrollableContent}</SidebarWrapper>
          )}
          {image !== undefined && <Background style={{ backgroundImage: `url(${image})` }} />}
        </Drawer>
      </Box>
    </div>
  );
};

Sidebar.propTypes = {
  image: PropTypes.string,
  routes: PropTypes.arrayOf(PropTypes.object),
  open: PropTypes.bool,
  newHeader: PropTypes.bool,
  handleDrawerClose: PropTypes.func,
  handleDrawerToggle: PropTypes.func,
  context: PropTypes.object,
};

export default Sidebar;
